import React, { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import BottomSheet from '../../../ui/BottomSheet';
import ParticipantHead from './ParticipantHead';
import ValuesPopover from './ValuesPopover';
import { useLifeDelta } from '../hooks/useLifeDelta';
import './ParticipantRow.css';

/** Sitzplätze am Brett (Positionen 0–3, Server: `PLAYTEST_MAX_PLAYERS`). */
const SEAT_COUNT = 4;

/**
 * Zeile der Teilnehmer-Köpfe am oberen Brettrand (Spec 2026-10-21 §5.5/§5.6, §14).
 *
 *   <ParticipantRow ref={measureRef} floating trailing={<TurnChip />}
 *     players={state.players} viewerUserId={me} currentTurnUserId={state.currentTurnUserId}
 *     isCommanderMode={state.isCommanderMode} connected={connected} isMobile={isMobile}
 *     compactOpponents={isMobile} canAct={canAct} previewBind={hover.bind}
 *     onLifeCommit={updateLife} onCounterChange={updatePlayerCounter} onCounterAdd={updatePlayerCounter}
 *     onCounterRemove={removePlayerCounter} onCommanderDamageChange={updateCommanderDamage}
 *     onZoneClick={(userId, zone) => …} onRequestHand={requestViewHand} />
 *
 * - `floating`: die Zeile liegt absolut am oberen Rand der Brettfläche, ohne Hintergrund; nur die
 *   Panels fangen den Zeiger, in den Lücken bleibt das Brett bedienbar. `trailing` steht als
 *   weiteres Panel am rechten Ende (Hinweise, Zug-Zeile am Handy). `ref` zeigt auf die Zeile, damit
 *   die Seite ihre Höhe messen kann.
 * - Eigener Kopf zuerst und breiter, danach die übrigen in Sitzreihenfolge ab dem eigenen Platz.
 * - Leben des eigenen Kopfs läuft über `useLifeDelta`: Klicks ändern die Zahl sofort, die Summe
 *   geht nach der Ruhezeit als `onLifeCommit(delta)` raus; ohne Verbindung (`connected` false)
 *   wartet sie auf das Wiederverbinden.
 * - Rahmen am Kopf des Teilnehmers am Zug; ohne Zug (`currentTurnUserId` null) trägt keiner
 *   einen Rahmen und die Zeile zeigt „Niemand am Zug".
 * - Klick auf eine Lebenszahl öffnet den Werte-Aufklapper dieses Teilnehmers (Popover am
 *   Rechner, Sheet am Handy); ändern lassen sich nur die eigenen Werte. Commander-Schaden gibt
 *   es nur im Commander-Modus, Quellen sind die übrigen Teilnehmer.
 * - `compactOpponents`: fremde Köpfe als einzeilige Chips; ein Klick klappt den Kopf auf — am
 *   Rechner an seinem Platz, am Handy in einem Sheet. Am Rechner klappt ein Klick außerhalb des
 *   Kopfes oder ein zweiter Klick auf eine freie Stelle des Kopfes ihn wieder ein (nicht, solange
 *   sein Werte-Aufklapper offen ist). Am Handy ist auch der eigene Kopf ein Chip (mit Plus/Minus
 *   in Fingergröße); ab vier Teilnehmern zeigen die fremden Chips dort keine Zonen (im Sheet
 *   stehen sie), damit jeder Chip mindestens 100 px breit bleibt.
 * - `onExpandedChange(open)`: ein Chip ist am Rechner an seinem Platz aufgeklappt (die Zeile ist
 *   dann vorübergehend höher; die Seite rechnet mit der eingeklappten Höhe weiter).
 * - `canAct={false}` (ausgeschieden): der eigene Kopf zeigt das Leben ohne Plus/Minus, die eigenen
 *   Werte sind nur lesbar.
 * - `onZoneClick(userId, zone)`, `onRequestHand(userId)` (ohne: Hand-Chip nur Anzeige) und
 *   `previewBind` (Vorschau der Oberkarte einer offenen Bibliothek) für die Zonen fremder Köpfe.
 * - Prüfanker: `data-testid="participant-row"` mit `data-floating`.
 */
const ParticipantRow = forwardRef(function ParticipantRow({
  players = [], viewerUserId, currentTurnUserId, compactOpponents = false, isMobile = false,
  isCommanderMode = false, connected = true, canAct = true, previewBind, floating = false, trailing = null,
  onExpandedChange, onLifeCommit, onCounterChange, onCounterRemove, onCounterAdd, onCommanderDamageChange, onZoneClick, onRequestHand,
}, ref) {
  const [valuesUserId, setValuesUserId] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const expandedRef = useRef(null);

  // Vor dem Zeichnen melden: die Seite misst die Zeile im selben Durchlauf neu.
  const expandedInPlaceAny = !isMobile && expandedUserId !== null;
  useLayoutEffect(() => {
    onExpandedChange?.(expandedInPlaceAny);
  }, [expandedInPlaceAny, onExpandedChange]);

  // Rechner: ein aufgeklappter Chip klappt bei einem Klick außerhalb wieder ein.
  useEffect(() => {
    if (isMobile || expandedUserId === null || valuesUserId === expandedUserId) return undefined;
    const collapseOutside = (event) => {
      if (expandedRef.current?.contains(event.target)) return;
      setExpandedUserId(null);
    };
    document.addEventListener('pointerdown', collapseOutside, true);
    return () => document.removeEventListener('pointerdown', collapseOutside, true);
  }, [isMobile, expandedUserId, valuesUserId]);

  const own = players.find((p) => p.userId === viewerUserId) || null;
  const { displayedLife, pendingDelta, add } = useLifeDelta({
    serverLife: own?.life ?? 0,
    onCommit: onLifeCommit,
    connected,
  });

  const ordered = orderBySeat(players, own);
  const valuesPlayer = players.find((p) => p.userId === valuesUserId) || null;
  const expandedPlayer = players.find((p) => p.userId === expandedUserId) || null;

  const popoverProps = (player) => ({
    player,
    isOwn: player.userId === viewerUserId && canAct,
    damageSources: isCommanderMode ? players.filter((p) => p.userId !== player.userId) : [],
    isMobile,
    onCounterChange,
    onCounterRemove,
    onCounterAdd,
    onCommanderDamageChange,
  });

  const renderHead = (player, { compact, inSheet = false } = {}) => {
    const isOwn = player.userId === viewerUserId;
    const expandedInPlace = !isMobile && !inSheet && !isOwn && expandedUserId === player.userId;
    return (
      <ParticipantHead
        key={player.userId}
        ref={expandedInPlace ? expandedRef : undefined}
        player={isOwn ? { ...player, life: displayedLife } : player}
        isOwn={isOwn}
        readOnly={isOwn && !canAct}
        isActiveTurn={player.userId === currentTurnUserId}
        compact={compact}
        zonesInChip={!(isMobile && players.length >= SEAT_COUNT)}
        pendingDelta={isOwn ? pendingDelta : 0}
        onLifeChange={add}
        onLongLifeChange={add}
        onOpenValues={() => setValuesUserId(player.userId)}
        onZoneClick={(zone) => onZoneClick?.(player.userId, zone)}
        onRequestHand={onRequestHand ? () => onRequestHand(player.userId) : undefined}
        onExpand={inSheet ? undefined : () => setExpandedUserId(player.userId)}
        previewBind={previewBind}
        // Zweiter Klick auf eine freie Stelle des aufgeklappten Kopfs klappt ihn wieder ein.
        onClick={expandedInPlace ? (event) => {
          if (!event.target.closest('button')) setExpandedUserId(null);
        } : undefined}
      />
    );
  };

  const placeHead = (player) => {
    const isOwn = player.userId === viewerUserId;
    const compact = isOwn
      ? isMobile
      : compactOpponents && !(expandedUserId === player.userId && !isMobile);
    const head = renderHead(player, { compact });
    if (isMobile) return head;
    return (
      <ValuesPopover
        key={player.userId}
        open={valuesUserId === player.userId}
        onOpenChange={(open) => setValuesUserId(open ? player.userId : null)}
        {...popoverProps(player)}
      >
        {head}
      </ValuesPopover>
    );
  };

  return (
    <div
      ref={ref}
      className={`participant-row${isMobile ? ' participant-row--mobile' : ''}${floating ? ' participant-row--floating' : ''}`}
      data-testid="participant-row"
      data-floating={floating ? 'true' : 'false'}
    >
      {ordered.map(placeHead)}
      {currentTurnUserId == null && <span className="participant-row__no-turn">Niemand am Zug</span>}
      {trailing && <div className="participant-row__trailing">{trailing}</div>}

      {isMobile && expandedPlayer && (
        <BottomSheet
          open
          onOpenChange={(open) => { if (!open) setExpandedUserId(null); }}
          title={expandedPlayer.displayName}
        >
          {renderHead(expandedPlayer, { compact: false, inSheet: true })}
        </BottomSheet>
      )}
      {isMobile && valuesPlayer && (
        <ValuesPopover
          open
          onOpenChange={(open) => { if (!open) setValuesUserId(null); }}
          {...popoverProps(valuesPlayer)}
        />
      )}
    </div>
  );
});

export default ParticipantRow;

/** Eigener Teilnehmer zuerst, danach im Uhrzeigersinn ab seinem Platz. */
function orderBySeat(players, own) {
  const start = own?.position ?? 0;
  const seatDistance = (p) => (p.position - start + SEAT_COUNT) % SEAT_COUNT;
  return [...players].sort((a, b) => {
    if (own && a.userId === own.userId) return -1;
    if (own && b.userId === own.userId) return 1;
    return seatDistance(a) - seatDistance(b);
  });
}
